import React, { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { formatTime } from './CustomPlayerIcons';
import { getChapterAtTime, getTrickPlayFrame, type TrickPlayChapter, type TrickPlayFrame, type TrickPlaySourceKind } from './customPlayerTrickPlay';
import styles from './CustomPlayerProgress.module.scss';

const PREVIEW_WIDTH = 180;

type Props = {
  currentTime: number;
  duration: number;
  progress: number;
  buffered?: number;
  isScrubbing?: boolean;
  scrubProgress?: number;
  chapters?: TrickPlayChapter[];
  showTrickPlay?: boolean;
  trickPlayFrames?: TrickPlayFrame[];
  trickPlayKind?: TrickPlaySourceKind;
  trickPlayFallback?: string;
  onSeek?: (progress: number) => void;
  onScrubStart?: () => void;
  onScrubEnd?: () => void;
  onScrub?: (progress: number) => void;
};

const TrickPlayThumbnail: React.FC<{ frame?: TrickPlayFrame; fallback?: string }> = ({ frame, fallback }) => {
  const url = frame?.url || fallback;
  if (!url) {
    return null;
  }

  if (!frame?.rect) {
    return <img src={url} alt="" className={styles.thumbnailImage} />;
  }

  const { x, y, width } = frame.rect;
  const scale = PREVIEW_WIDTH / Math.max(width, 1);

  return (
    <img
      src={url}
      alt=""
      className={styles.thumbnailSprite}
      style={{
        left: -x * scale,
        top: -y * scale,
        transform: `scale(${scale})`,
      }}
    />
  );
};

const CustomPlayerProgress: React.FC<Props> = ({
  currentTime,
  duration,
  progress,
  buffered = 0,
  isScrubbing = false,
  scrubProgress = 0,
  chapters = [],
  showTrickPlay = false,
  trickPlayFrames = [],
  trickPlayKind = 'none',
  trickPlayFallback,
  onSeek,
  onScrubStart,
  onScrubEnd,
  onScrub,
}) => {
  const { t } = useTranslation('player');
  const trackRef = useRef<HTMLDivElement>(null);
  const [hoverProgress, setHoverProgress] = useState<number | null>(null);
  const playedProgress = isScrubbing ? scrubProgress : progress;
  const previewProgress = isScrubbing ? scrubProgress : hoverProgress;
  const scrubberProgress = previewProgress ?? playedProgress;
  const showPreview = showTrickPlay && previewProgress != null;

  const getProgressFromEvent = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) {
      return 0;
    }
    const rect = track.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const handleTrackClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const newProgress = getProgressFromEvent(event.clientX);
    onSeek?.(newProgress);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (isScrubbing) {
      return;
    }

    setHoverProgress(getProgressFromEvent(event.clientX));
  };

  const handleMouseLeave = () => {
    if (!isScrubbing) {
      setHoverProgress(null);
    }
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    onScrubStart?.();
    onScrub?.(getProgressFromEvent(event.clientX));

    const handleMouseMoveDocument = (moveEvent: MouseEvent) => {
      onScrub?.(getProgressFromEvent(moveEvent.clientX));
    };

    const handleMouseUp = (upEvent: MouseEvent) => {
      onScrubEnd?.();
      onSeek?.(getProgressFromEvent(upEvent.clientX));
      setHoverProgress(null);
      document.removeEventListener('mousemove', handleMouseMoveDocument);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMoveDocument);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const previewTime = (previewProgress ?? 0) * duration;
  const previewFrame = showPreview ? getTrickPlayFrame(trickPlayFrames, previewTime) : undefined;
  const chapterTitle = showPreview ? getChapterAtTime(chapters, previewTime)?.title : undefined;

  const previewLeft = (() => {
    if (previewProgress == null) {
      return '0%';
    }

    const trackWidth = trackRef.current?.getBoundingClientRect().width ?? 0;
    if (!trackWidth) {
      return `${previewProgress * 100}%`;
    }

    const raw = previewProgress * trackWidth;
    const half = PREVIEW_WIDTH / 2;
    const clamped = Math.min(Math.max(raw, half), Math.max(half, trackWidth - half));
    return `${clamped}px`;
  })();

  return (
    <div className={styles.progressContainer}>
      <div
        ref={trackRef}
        className={styles.track}
        onClick={handleTrackClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(playedProgress * 100)}
        aria-label={t('seek')}
        tabIndex={0}
      >
        <div className={styles.buffered} style={{ width: `${buffered * 100}%` }} />
        <div className={styles.played} style={{ width: `${playedProgress * 100}%` }} />
        <div
          className={classNames(styles.scrubber, { [styles.visible]: isScrubbing || hoverProgress != null })}
          style={{ left: `${scrubberProgress * 100}%` }}
        />

        {chapters.map((chapter) => (
          <div
            key={chapter.id}
            className={styles.chapterMarker}
            style={{ left: `${duration > 0 ? (chapter.startTime / duration) * 100 : 0}%` }}
            title={chapter.title}
            onClick={(event) => {
              event.stopPropagation();
              onSeek?.(chapter.startTime / duration);
            }}
          />
        ))}

        {showPreview && (
          <div
            className={styles.trickPlayPreview}
            style={{ left: previewLeft }}
            data-trickplay-kind={trickPlayKind}
          >
            {chapterTitle ? <span className={styles.previewLabel}>{chapterTitle}</span> : null}
            <div className={styles.thumbnail}>
              <TrickPlayThumbnail frame={previewFrame} fallback={trickPlayFallback} />
              <span className={styles.previewTime}>{formatTime(previewTime, { forceHours: true })}</span>
            </div>
          </div>
        )}
      </div>

      <div className={styles.timeRow}>
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
};

export default CustomPlayerProgress;
